import {
  useCallback,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router';

import {
  createInitialNewsletterFormState,
  getNewsletterAdminListItems,
  toNewsletterEditFormState,
  toNewsletterUpsertPayload,
  useNewsletterAdmin,
  type NewsletterAdminFormState,
  type NewsletterAdminListItemViewModel,
  type NewsletterPage,
} from '@/domains/newsletter';
import type { ErrorResponse } from '@/types';
import useToast from '@/shared/stores/toastStore';

interface NewsletterAdminHeaderProps {
  onBack: () => void;
  onViewPublished: () => void;
}

interface NewsletterAdminFormProps {
  editorKey: string;
  editingLabel: string | null;
  formState: NewsletterAdminFormState;
  formTitle: string;
  isSaving: boolean;
  onContentChange: (value: string) => void;
  onPublishedChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onReset: () => void;
  onStartAtChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onTitleChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

interface NewsletterAdminSavedNewslettersProps {
  isDeletingNewsletter: boolean;
  isLoading: boolean;
  isSaving: boolean;
  items: NewsletterAdminListItemViewModel[];
  onDelete: (page: NewsletterPage) => void;
  onEdit: (page: NewsletterPage) => void;
  totalCount: number;
}

interface NewsletterAdminDeleteModalProps {
  isDeletingNewsletter: boolean;
  isOpen: boolean;
  newsletterTitle: string;
  onClose: () => void;
  onConfirm: () => void;
}

interface UseNewsletterAdminControllerResult {
  deleteModalProps: NewsletterAdminDeleteModalProps;
  formProps: NewsletterAdminFormProps;
  headerProps: NewsletterAdminHeaderProps;
  savedNewslettersProps: NewsletterAdminSavedNewslettersProps;
}

const toErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    const axiosError = error as AxiosError<ErrorResponse>;
    return axiosError.response?.data?.title || fallback;
  }

  return fallback;
};

export const useNewsletterAdminController =
  (): UseNewsletterAdminControllerResult => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const {
      createNewsletter,
      deleteNewsletter,
      isCreatingNewsletter,
      isDeletingNewsletter,
      isLoading,
      isUpdatingNewsletter,
      pages,
      updateNewsletter,
    } = useNewsletterAdmin();
    const [formState, setFormState] = useState<NewsletterAdminFormState>(
      createInitialNewsletterFormState,
    );
    const [editorKey, setEditorKey] = useState('new');
    const [newsletterToDelete, setNewsletterToDelete] =
      useState<NewsletterPage | null>(null);

    const isSaving = isCreatingNewsletter || isUpdatingNewsletter;
    const savedNewsletterItems = useMemo(() => {
      return getNewsletterAdminListItems(pages);
    }, [pages]);

    const handleReset = useCallback(() => {
      setFormState(createInitialNewsletterFormState());
      setEditorKey(`new-${Date.now()}`);
    }, []);

    const handleTitleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setFormState((prev) => ({
          ...prev,
          title: value,
        }));
      },
      [],
    );

    const handleStartAtChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setFormState((prev) => ({
          ...prev,
          startAt: value,
        }));
      },
      [],
    );

    const handlePublishedChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        setFormState((prev) => ({
          ...prev,
          published: checked,
        }));
      },
      [],
    );

    const handleContentChange = useCallback((value: string) => {
      setFormState((prev) => ({
        ...prev,
        content: value,
      }));
    }, []);

    const handleEdit = useCallback((page: NewsletterPage) => {
      setFormState(toNewsletterEditFormState(page));
      setEditorKey(page.id);
    }, []);

    const handleDelete = useCallback((page: NewsletterPage) => {
      setNewsletterToDelete(page);
    }, []);

    const handleCloseDeleteModal = useCallback(() => {
      if (isDeletingNewsletter) return;
      setNewsletterToDelete(null);
    }, [isDeletingNewsletter]);

    const handleSubmit = useCallback(
      async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!formState.title.trim()) {
          showToast('error', {
            title: 'Title is required',
            message: 'Add a title before saving.',
          });
          return;
        }

        const payload = toNewsletterUpsertPayload(formState);

        try {
          if (formState.id) {
            await updateNewsletter({
              id: formState.id,
              payload,
            });
            showToast('success', {
              title: `Newsletter ${payload.title} updated`,
              message: 'Changes saved in database.',
            });
          } else {
            await createNewsletter(payload);
            showToast('success', {
              title: `Newsletter ${payload.title} created`,
              message: 'Saved in database.',
            });
          }

          handleReset();
        } catch (error) {
          showToast('error', {
            title: formState.id
              ? `Unable to update newsletter ${formState.title}`
              : `Unable to create newsletter ${formState.title}`,
            message: toErrorMessage(
              error,
              'An unexpected error occurred.',
            ),
          });
        }
      },
      [
        createNewsletter,
        formState,
        handleReset,
        showToast,
        updateNewsletter,
      ],
    );

    const handleConfirmDelete = useCallback(async () => {
      if (!newsletterToDelete) return;

      const targetPage = newsletterToDelete;

      try {
        await deleteNewsletter(targetPage.id);

        if (formState.id === targetPage.id) {
          handleReset();
        }

        showToast('success', {
          title: `Newsletter ${targetPage.title} removed`,
          message: 'Removed from database.',
        });
        setNewsletterToDelete(null);
      } catch (error) {
        showToast('error', {
          title: `Unable to delete newsletter ${targetPage.title}`,
          message: toErrorMessage(
            error,
            'An unexpected error occurred.',
          ),
        });
      }
    }, [
      deleteNewsletter,
      formState.id,
      handleReset,
      newsletterToDelete,
      showToast,
    ]);

    return {
      deleteModalProps: {
        isDeletingNewsletter,
        isOpen: Boolean(newsletterToDelete),
        newsletterTitle: newsletterToDelete?.title || 'Untitled',
        onClose: handleCloseDeleteModal,
        onConfirm: handleConfirmDelete,
      },
      formProps: {
        editorKey,
        editingLabel: formState.id
          ? `Editing ${formState.title || 'Untitled'}`
          : null,
        formState,
        formTitle: formState.id ? 'Edit newsletter' : 'Create newsletter',
        isSaving,
        onContentChange: handleContentChange,
        onPublishedChange: handlePublishedChange,
        onReset: handleReset,
        onStartAtChange: handleStartAtChange,
        onSubmit: handleSubmit,
        onTitleChange: handleTitleChange,
      },
      headerProps: {
        onBack: () => navigate('/settings'),
        onViewPublished: () => navigate('/newsletter'),
      },
      savedNewslettersProps: {
        isDeletingNewsletter,
        isLoading,
        isSaving,
        items: savedNewsletterItems,
        onDelete: handleDelete,
        onEdit: handleEdit,
        totalCount: pages.length,
      },
    };
  };
